import styled, { keyframes } from 'styled-components';
// ============================================
// KEYFRAME ANIMATIONS
// ============================================
const fadeUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
// ============================================
// PAGE LAYOUT
// ============================================
export const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
`;
// ============================================
// HERO SECTION
// ============================================
export const HeroSection = styled.section `
  padding: 48px 24px 32px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border-bottom: 1px solid var(--border-color);
`;
export const HeroTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 38px !important;
  font-weight: 600 !important;
  letter-spacing: 0.03em;
  margin-bottom: 10px !important;
  color: var(--text-primary) !important;

  span {
    color: var(--accent-gold, #B8860B);
  }

  @media (max-width: 768px) {
    font-size: 28px !important;
  }
`;
export const HeroSubtitle = styled.p `
  color: var(--text-secondary);
  font-size: 15px;
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.6;
`;
// ============================================
// PROFILE HEADER (Avatar + Edit Button)
// ============================================
export const ProfileHeaderBar = styled.div `
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  max-width: 1200px;
  margin: -40px auto 32px;
  padding: 0 24px;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    flex-direction: column;
    margin-top: -30px;
  }
`;
export const AvatarWrapper = styled.div `
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid var(--bg-primary);
  background: var(--bg-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-initial {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 40px;
    font-weight: 600;
    color: var(--accent-gold, #B8860B);
  }
`;
export const EditAvatarWrapper = styled.div `
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid var(--border-color);
  background: var(--bg-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-initial {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 48px;
    font-weight: 600;
    color: var(--text-tertiary);
  }
`;
export const EditButton = styled.button `
  background: var(--accent-gold, #B8860B);
  color: #FFFFFF;
  border: 1px solid var(--accent-gold, #B8860B);
  padding: 12px 28px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-gold-dark, #8B6914);
    border-color: var(--accent-gold-dark, #8B6914);
  }
`;
// ============================================
// MAIN CONTENT
// ============================================
export const MainContent = styled.div `
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 24px;
`;
// ============================================
// INFO CARDS GRID
// ============================================
export const ProfileGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
export const InfoCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);

    &::before {
      opacity: 1;
    }
  }
`;
export const InfoCardHeader = styled.div `
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
`;
export const InfoCardTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 !important;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: var(--accent-gold, #B8860B);
    font-size: 16px;
  }
`;
export const InfoCardBody = styled.div `
  padding: 0;
`;
export const InfoRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }

  .label {
    color: var(--text-tertiary);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
  }

  .value {
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 500;
    text-align: right;

    &.not-set {
      color: var(--text-tertiary);
      font-style: italic;
    }

    &.highlighted {
      color: var(--accent-gold, #B8860B);
      font-weight: 600;
    }
  }
`;
// ============================================
// EDIT MODE - FORM SECTIONS
// ============================================
export const FormSection = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
  }
`;
export const FormSectionHeader = styled.div `
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
`;
export const FormSectionTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 !important;
`;
export const FormSectionBody = styled.div `
  padding: 24px 20px;
`;
export const FormGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
export const FormGroup = styled.div `
  margin-bottom: 0;
`;
export const FormLabel = styled.label `
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;
export const FormInput = styled.input `
  width: 100%;
  padding: 14px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;
export const FormSelect = styled.select `
  width: 100%;
  padding: 14px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
  }

  &:hover {
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
  }
`;
export const FormHint = styled.p `
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
  line-height: 1.5;
`;
// ============================================
// PROFILE PICTURE UPLOAD
// ============================================
export const ProfilePictureSection = styled.div `
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;
export const ProfilePictureInfo = styled.div `
  flex: 1;
`;
export const FileInput = styled.input `
  width: 100%;
  padding: 10px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 13px;
  margin-bottom: 8px;

  &::file-selector-button {
    background: var(--accent-gold, #B8860B);
    color: #FFFFFF;
    border: none;
    padding: 8px 16px;
    margin-right: 12px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;
// ============================================
// ACTION BUTTONS
// ============================================
export const ButtonGroup = styled.div `
  display: flex;
  gap: 16px;
  margin-top: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
export const PrimaryButton = styled.button `
  background: var(--accent-gold, #B8860B);
  color: #FFFFFF;
  border: 1px solid var(--accent-gold, #B8860B);
  padding: 14px 32px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-gold-dark, #8B6914);
    border-color: var(--accent-gold-dark, #8B6914);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const SecondaryButton = styled.button `
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 14px 32px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-gold, #B8860B);
    color: var(--accent-gold, #B8860B);
  }
`;
// ============================================
// LOADING STATE
// ============================================
export const LoadingWrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
`;
export const LoadingSpinner = styled.div `
  width: 48px;
  height: 48px;
  border: 3px solid var(--border-color);
  border-top-color: var(--accent-gold, #B8860B);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
export const LoadingText = styled.p `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 18px;
  color: var(--text-secondary);
`;
